import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import {
  isLoggedInUser,
  promptLoginPage,
} from '../../../contexts/User/helpers/userContextHelpers';
import { getParticipantOrderId } from '../../../selectors/participants';
import { renderSeoMetatagsPage } from '../../../services/SeoMetatagsPage';
import { needToRedirectToLogin } from './needToRedirectToLogin';
import {
  addOnLoginHandler,
  IOnLoginHandlerPriority,
} from '../../../contexts/GeneralDataProvider/helpers/onLogin';
import { getInitialProps } from './getInitialProps';
import { goToVisitorPage } from './goToVisitorPage';
import { createOrderOrSendJoinRequest } from './createOrderOrSendJoinRequest';
import { isRedirectingToVisitorOrParticipantPage } from './isRedirectingToVisitorOrParticipantPage';
import isPlansOnly from './isPlansOnly';

export const createParticipantFlow = async ({
  flowAPI,
}: {
  flowAPI: ControllerFlowAPI;
}) => {
  const initialProps = await getInitialProps(flowAPI);
  const isEditor =
    !flowAPI.environment.isViewer && !flowAPI.environment.isPreview;

  if (isEditor) {
    flowAPI.controllerConfig.setProps({
      ...initialProps,
    });
  }

  addOnLoginHandler({
    priority: IOnLoginHandlerPriority.SECONDARY,
    handler: async (flowAPI: ControllerFlowAPI) => {
      // we just logged in, so we need to check for redirects before proceeding with order
      const initialProps = await getInitialProps(flowAPI);

      if (
        await isRedirectingToVisitorOrParticipantPage({ initialProps, flowAPI })
      ) {
        return;
      }

      return createOrderOrSendJoinRequest({
        initialProps,
        flowAPI,
      });
    },
  });

  if (
    isPlansOnly(
      initialProps.challengeData?.challenge,
      initialProps.userPaidPlans || [],
    )
  ) {
    return goToVisitorPage({
      challengeData: initialProps.challengeData,
      flowAPI,
    });
  }

  // not logged in, private program
  // go login to see if already purchased
  if (needToRedirectToLogin({ initialProps, flowAPI })) {
    flowAPI.controllerConfig.setProps({
      isForcedBlankPage: true,
    });

    try {
      await promptLoginPage(flowAPI);
    } catch {
      // user closed the login popup - redirect to visitor page
      void goToVisitorPage({
        challengeData: initialProps.challengeData,
        flowAPI,
      });
    }

    return;
  }

  // logged in, public / private & approved, already joined
  // private program, waiting for owners approval
  if (
    await isRedirectingToVisitorOrParticipantPage({ initialProps, flowAPI })
  ) {
    return;
  }

  // logged in, public / private & approved, not purchased yet
  if (
    isLoggedInUser(flowAPI) &&
    !getParticipantOrderId(initialProps?.participant) &&
    flowAPI.environment.isViewer
  ) {
    return createOrderOrSendJoinRequest({
      initialProps,
      flowAPI,
    });
  }

  if (!isEditor) {
    flowAPI.controllerConfig.setProps({ ...initialProps });
  }
  renderSeoMetatagsPage(
    flowAPI,
    initialProps?.challengeData?.challenge,
    'PAYMENT_PAGE',
  );
};
